// The photo dedup, on the admin order page (owner, 2026-10-24, migration 267):
// one small line « 12 doublons retirés · 4 photos similaires » and a « Voir »
// that opens the pairs side by side (kept vs hidden) with « Restaurer ».
// Pairs placed TWICE on pages of a built book (never edited by a machine) get
// « Retirer de l'album ». Nothing here messages the client.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ordersApi, photosApi, type PhotoDedupItem } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { useT } from '@/lib/i18n';

const KIND_LABEL: Record<PhotoDedupItem['kind'], string> = {
  hidden: 'Doublon masqué',
  similar: 'Photo similaire (gardée dans le plateau)',
  placed_twice: 'Deux fois dans l’album',
  other_album: 'Aussi dans un autre album',
};

export function PhotoDedupLine({ shopifyOrderId }: { shopifyOrderId: string }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = ['order-photo-dedup', shopifyOrderId];
  const { data } = useQuery({
    queryKey: key,
    queryFn: () => ordersApi.photoDedup(shopifyOrderId),
    enabled: !!shopifyOrderId,
    staleTime: 30_000,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: ['order-photos-summary', shopifyOrderId] });
  };
  const restore = useMutation({
    mutationFn: (id: string) => photosApi.restoreDuplicate(id),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e: Error) => setError(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => ordersApi.removeDuplicateFromAlbum(shopifyOrderId, id),
    onSuccess: (r) => { setError(r.ok ? null : (r.error ?? t('Refusé'))); refresh(); },
    onError: (e: Error) => setError(e.message),
  });

  if (!data) return null;
  const total = data.hidden + data.similar + data.placedTwice + data.otherAlbum;
  if (!total) return null;

  const parts = [
    data.hidden ? t('{n} doublons retirés', { n: data.hidden }) : null,
    data.similar ? t('{n} photos similaires', { n: data.similar }) : null,
    data.placedTwice ? t('{n} en double dans l’album', { n: data.placedTwice }) : null,
    data.otherAlbum ? t('{n} aussi dans un autre album', { n: data.otherAlbum }) : null,
  ].filter(Boolean);

  return (
    <>
      <div className="flex items-center justify-between gap-2 mb-3 text-xs text-gray-500">
        <span>{parts.join(' · ')}</span>
        <button type="button" className="text-xs font-medium text-brand-500 hover:underline" onClick={() => setOpen(true)}>
          {t('Voir')}
        </button>
      </div>
      <Modal isOpen={open} onClose={() => setOpen(false)} title={t('Doublons et photos similaires')} size="lg">
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        <ul className="space-y-3 max-h-[70vh] overflow-y-auto">
          {data.items.map((i) => (
            <li key={i.id} className="flex items-center gap-3 text-xs">
              <figure className="w-20 shrink-0">
                {i.keeper_tile_url ? <img src={i.keeper_tile_url} alt="" className="w-20 h-20 object-cover rounded" /> : <div className="w-20 h-20 bg-gray-100 rounded" />}
                <figcaption className="text-gray-400 mt-0.5">{t('Gardée')}</figcaption>
              </figure>
              <figure className="w-20 shrink-0">
                {i.tile_url ? <img src={i.tile_url} alt="" className="w-20 h-20 object-cover rounded opacity-70" /> : <div className="w-20 h-20 bg-gray-100 rounded" />}
                <figcaption className="text-gray-400 mt-0.5">{t(KIND_LABEL[i.kind])}</figcaption>
              </figure>
              <div className="flex flex-col gap-1 ml-auto items-end">
                {(i.kind === 'hidden' || i.kind === 'similar') && (
                  <button type="button" disabled={restore.isPending} onClick={() => restore.mutate(i.id)}
                    className="text-xs font-medium text-brand-500 hover:underline">
                    {t('Restaurer')}
                  </button>
                )}
                {i.kind === 'placed_twice' && (
                  <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(i.id)}
                    className="text-xs font-medium text-red-600 hover:underline">
                    {t("Retirer de l'album")}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}
